import { Component, effect, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import {
  AuraChatComponent,
  AuraUiChatAgent,
  AuraUiChatConfig,
  AuraUiChatAnalyticsEvent,
  AuraUiChatRuntimeResponseEvent,
} from '@powerfleet/aura_standalone.ui';
import { AgentsService } from '../../../api/api/agents.service';
import { ThemeService } from '../../services/theme.service';
import { EnvironmentService } from '../../services/environment.service';
import { HostApplicationService } from '../../services/host-application.service';
import { AuraAnalyticsService } from '../../services/analytics/aura-analytics.service';
import { AUTH_SERVICE_TOKEN, IAuthService } from '../../services/auth/auth.interface';

/**
 * Host shell around the packaged managed runtime (`aura-chat`).
 * The runtime now owns the chat UI, history, streaming and data fetching; this
 * component only resolves host context (agent, platform, theme, language) and
 * forwards the runtime's analytics events to the host analytics backend.
 */
@Component({
  selector: 'aura-chat-wrapper',
  standalone: true,
  imports: [CommonModule, AuraChatComponent],
  templateUrl: './chat-wrapper.component.html',
  styleUrls: ['./chat-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatWrapperComponent implements OnInit, OnDestroy {
  isLoading = true;
  themeOverride = '';
  isThemeDark = false;
  host: string;
  language: string | null = null;
  chatConfig: AuraUiChatConfig = {};
  context: Record<string, unknown> | null = null;
  selectedAgent: AuraUiChatAgent | null = null;
  userId: string | null = null;
  analyticsContext: Record<string, string> = {};
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private themeService: ThemeService,
    private environmentService: EnvironmentService,
    private hostApplicationService: HostApplicationService,
    private agentsService: AgentsService,
    private analyticsService: AuraAnalyticsService,
    @Inject(AUTH_SERVICE_TOKEN) private authService: IAuthService,
  ) {
    this.host = this.environmentService.host;
    this.isThemeDark = this.themeService.current === 'dark';
    this.themeOverride = this.route.snapshot.data['themeOverride'] ?? '';
    // Host context arrives asynchronously (loadContext resolves after bootstrap),
    // so track it reactively rather than reading the signal once at construction —
    // otherwise `context` (and thus the backend `platform_context`) stays null.
    effect(() => {
      const hostContext = this.hostApplicationService.getCurrentContext();
      this.context = (hostContext ?? null) as unknown as Record<string, unknown> | null;
      this.language = this.resolveLanguage();
    });
    this.chatConfig = this.mapConfig(this.environmentService.chatConfig);
    this.analyticsContext = { host: this.host };
    this.authService.userInfo$.subscribe(info => (this.userId = (info || {}).userId ?? null));
    this.subscriptions.add(
      this.hostApplicationService.onThemeUpdated$.subscribe((theme) => {
        this.isThemeDark = theme === 'dark';
      }),
    );
  }

  async ngOnInit(): Promise<void> {
    const hostTheme = await this.hostApplicationService.getCurrentTheme();
    if (hostTheme) {
      this.isThemeDark = hostTheme === 'dark';
    }
    await this.resolveSelectedAgent();
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onAgentChange(agent: AuraUiChatAgent): void {
    this.selectedAgent = agent;
    try {
      localStorage.setItem('agentId', agent.id);
    } catch {
      /* localStorage unavailable */
    }
  }

  onThemeToggle(): void {
    this.isThemeDark = !this.isThemeDark;
    this.themeService.current = this.isThemeDark ? 'dark' : 'light';
  }

  onAnalyticsEvent(event: AuraUiChatAnalyticsEvent): void {
    this.analyticsService.trackEvent(event.name, event.properties);
  }

  onResponseReceived(_event: AuraUiChatRuntimeResponseEvent): void {
    // Reserved for host-side actions (e.g. postMessage to the parent window).
  }

  private resolveLanguage(): string {
    const base = (this.context as Record<string, { language?: string }> | null)?.['embedded.aura.unityaiot.com'];
    return base?.language ?? 'en-US';
  }

  private mapConfig(chatConfig: Record<string, unknown>): AuraUiChatConfig {
    return {
      showAgentSelector: chatConfig['showAgentSelector'] as boolean,
      showThemeSelector: chatConfig['showThemeSelector'] as boolean,
      showChatInputKbSources: chatConfig['showChatInputKbSources'] as boolean,
      showChatInputPreferences: chatConfig['showChatInputPreferences'] as boolean,
      showDeepResearch: chatConfig['showDeepResearch'] as boolean,
      showSmallModel: chatConfig['showSmallModel'] as boolean,
      allowToggleTempChat: chatConfig['allowToggleTempChat'] as boolean,
      initialSidebarCollapsed: chatConfig['initialSidebarCollapsed'] as boolean,
    };
  }

  private async resolveSelectedAgent(): Promise<void> {
    const chatConfig = this.environmentService.chatConfig;
    const queryAgent = this.route.snapshot.queryParamMap.get('agent');
    let storedAgentId: string | null = null;
    try {
      storedAgentId = localStorage.getItem('agentId');
    } catch {
      storedAgentId = null;
    }
    const agentId = chatConfig.selectedAgent || queryAgent || storedAgentId;
    try {
      const agents = await lastValueFrom(this.agentsService.getAvailableAgents());
      // Fall back to the first available agent so the runtime always has one to render.
      const match = (agentId && agents.find(agent => agent.id === agentId)) || agents[0];
      if (match) {
        this.selectedAgent = {
          id: match.id,
          name: match.name,
          description: match.userFacingDescription || match.description,
          // Bundled Aura logo shipped in the package assets (copied to /aura-assets via angular.json).
          avatarUrl: 'aura-assets/images/logo-aura.svg',
        };
      }
    } catch (error) {
      console.error('Failed to resolve selected agent', error);
    }
  }
}
